/**
 * The dictation models the desktop app can run on this machine, and the
 * user's choice among them. Rust owns the catalogue, the choice and the
 * downloads (`dictation_models.rs`). None comes with the app: each downloads
 * when the user chooses it. Local dictation runs the chosen model once it is
 * installed - Whisper Base ("Standard") until then where that is in, else the
 * system recogniser - switching by itself when a download finishes.
 */
import { invoke } from "@tauri-apps/api/core";

import { isTauri } from "@/lib/is-tauri";
import { onAssetProgress, type VoiceAssetProgress } from "./voice-assets";

/** A catalogue id: `dictation-models.env`'s DICTATION_MODELS. The catalogue
 *  is Rust's; the UI shows whichever models it lists. */
export type DictationModelId = string;

export type DictationModelState = "installed" | "downloading" | "missing";

export type DictationModelStatus = {
  id: DictationModelId;
  /** Dictation language codes it serves; `null` for every language Whisper
   *  knows (Standard). */
  languages: string[] | null;
  /** Whisper Base ("Standard"): the fallback while a chosen model
   *  downloads, never removed; downloaded only when chosen. */
  builtin: boolean;
  state: DictationModelState;
  total_bytes: number;
  /** What a download still has to fetch. */
  missing_bytes: number;
  /** Measured accuracy in percent by dictation language code, for the
   *  languages it was measured in (`dictation-models.env`). */
  accuracy: Readonly<Record<string, number>>;
};

export type DictationModels = {
  /** The user's choice. */
  selected: DictationModelId;
  /** The model dictating now: the choice once installed, else Standard. */
  active: DictationModelId;
  models: DictationModelStatus[];
};

export type DictationModelProgress = VoiceAssetProgress & {
  model: DictationModelId;
};

/** Standard (Whisper Base), whose download reports Whisper's progress. */
const BUILTIN_MODEL: DictationModelId = "whisper-base";

/** Every model with its size and state; `null` outside the desktop app, or
 *  on a platform without local dictation models. */
export async function dictationModels(): Promise<DictationModels | null> {
  if (!isTauri()) return null;
  try {
    return await invoke<DictationModels>("denker_dictation_models");
  } catch {
    return null;
  }
}

/** Choose `model`; it dictates once installed (`downloadDictationModel`). */
export function selectDictationModel(
  model: DictationModelId,
): Promise<DictationModels> {
  return invoke<DictationModels>("denker_dictation_model_select", { model });
}

/** Download `model` if it is missing; resolves with every model's status
 *  once it is installed, rejects on the first failure. Safe to call again:
 *  whatever completed is kept, and a second call joins the first. */
export function downloadDictationModel(
  model: DictationModelId,
): Promise<DictationModels> {
  return invoke<DictationModels>("denker_dictation_model_download", { model });
}

/** Remove a downloaded `model` and free its disk space; removing the chosen
 *  one forgets the choice, so nothing downloads in its place. */
export function removeDictationModel(
  model: DictationModelId,
): Promise<DictationModels> {
  return invoke<DictationModels>("denker_dictation_model_remove", { model });
}

/** The model that suits the languages the user speaks, in their order
 *  (`dictation_model_rule.rs`). */
export function dictationModelForLanguages(
  languages: readonly string[],
): Promise<DictationModelId> {
  return invoke<DictationModelId>("denker_dictation_model_for_languages", {
    languages,
  });
}

/**
 * Make `model` the choice and start its download if it is missing, without
 * waiting for it: dictation switches over by itself once it is in, and
 * Settings › Voice shows the progress. A failure is logged by Rust; startup
 * and Settings offer the download again.
 */
export async function startDictationModel(
  model: DictationModelId,
): Promise<void> {
  if (!isTauri()) return;
  const after = await selectDictationModel(model);
  const status = after.models.find((candidate) => candidate.id === model);
  if (status?.state === "missing")
    void downloadDictationModel(model).catch(() => {});
}

/**
 * Download the model the user chose, if it is missing, because they listen
 * with the local engine; with none chosen yet, nothing. Startup does this by
 * itself only where the local engine is the machine's default. Never waited
 * for; a failure is logged by Rust.
 */
export function prepareWantedDictationModel(): void {
  if (!isTauri()) return;
  void invoke("denker_dictation_model_prepare").catch(() => {});
}

/** Download progress of any dictation model, Standard included. */
export function onDictationModelProgress(
  handler: (progress: DictationModelProgress) => void,
): () => void {
  const offDownloads = onAssetProgress<DictationModelProgress>(
    "dictation-model-progress",
    handler,
  );
  const offBuiltin = onAssetProgress("whisper-assets-progress", (progress) =>
    handler({ ...progress, model: BUILTIN_MODEL }),
  );
  return () => {
    offDownloads();
    offBuiltin();
  };
}
